import { useEffect, useState } from "react";

import { useAuth } from "./authContext";
import { getMyCompanies } from "./companies";
import { isImpersonating } from "./impersonate";

/**
 * Limited-client permission model.
 *
 * The Manager portal was collapsed into a reduced-permission Client. A
 * "limited client" is a `role='client'` user whose visibility is scoped
 * entirely by `company_manager_assignments` (via the
 * `is_manager_of_competition` RLS predicate) and who is NOT a member of any
 * company (`company_users`). Because every full-client write policy gates on
 * `is_company_member` — which is false for them — the DATABASE blocks
 * create / edit / delete / invite regardless of the UI.
 *
 * This hook drives the COSMETIC gate that hides those affordances so a
 * limited client never sees a button that would fail. RLS is the real wall;
 * this is just polish.
 *
 * Signal: a full client always has a `company_users` row (that's how clients
 * are provisioned). A limited client has none. So `isLimited` is simply
 * "role is client, not impersonating, and getMyCompanies() came back empty".
 * We default to NOT limited until we positively confirm, so a full client's
 * controls never flicker hidden during the membership fetch.
 */
export type LimitedClientState = {
  /** True until the membership lookup resolves. */
  loading: boolean;
  /** True when the signed-in user is a reduced-permission (limited) client. */
  isLimited: boolean;
};

export function useIsLimitedClient(): LimitedClientState {
  const { user, role, loading: authLoading } = useAuth();
  const [state, setState] = useState<LimitedClientState>({ loading: true, isLimited: false });

  useEffect(() => {
    if (authLoading) {
      setState({ loading: true, isLimited: false });
      return;
    }
    // Only client-role users can be "limited". Admins viewing a client via
    // impersonation keep their admin role and full affordances.
    if (!user || role !== "client" || isImpersonating()) {
      setState({ loading: false, isLimited: false });
      return;
    }
    let cancelled = false;
    void getMyCompanies().then((companies) => {
      if (cancelled) return;
      setState({ loading: false, isLimited: companies.length === 0 });
    });
    return () => {
      cancelled = true;
    };
  }, [user, role, authLoading]);

  return state;
}
